import { Frequency, SuggestedTasksQuery } from "generated-graphql/graphql";
import { useMemo } from "react";
import { TaskDialog } from "../TaskDialog";
import { TaskFormType } from "../types";
import { EditSuggestedTaskInput } from "./useHandleSuggestedTasks";

type SuggestedTask = SuggestedTasksQuery["suggestedTasks"]["items"][0];

interface SuggestedTaskDialogProps {
  task: SuggestedTask | null;
  onClose: () => void;
  onAccept: (task: EditSuggestedTaskInput) => void;
  onDismiss: (task: EditSuggestedTaskInput) => void;
  loading?: boolean;
  readonly?: boolean;
}

export const SuggestedTaskDialog = ({
  task,
  onClose,
  onAccept,
  onDismiss,
  loading = false,
  readonly = false,
}: SuggestedTaskDialogProps) => {
  // Convert SuggestedTask to TaskFormType
  const initialValues: TaskFormType = useMemo(() => {
    if (!task) {
      return {
        title: "",
        description: "",
        assignee: null,
        deadline: null,
        status: null,
        frequency: Frequency.Once,
        interval: 1,
        reminderDays: [],
        subtasks: [],
        facilityIds: [],
        documents: [],
        tags: [],
        links: [],
        watchers: [],
        isCompleted: false,
        completedOn: null,
        isSuggested: true,
      };
    }

    return {
      title: task.title,
      description: task.description ?? "",
      assignee: null,
      deadline: task.startDate ? new Date(task.startDate) : null,
      status: null,
      frequency: task.frequency,
      interval: task.interval,
      reminderDays: [],
      subtasks: [],
      facilityIds: task.facilities?.map((f) => f.id) ?? [],
      // Documents from suggested tasks only have id and title
      // The TaskDialog will handle displaying them appropriately
      documents: task.documents,
      tags: [], // Not initialized for suggested tasks
      links: [], // Not initialized for suggested tasks
      watchers: [], // Not initialized for suggested tasks
      isCompleted: false,
      completedOn: null,
      isSuggested: true,
    };
  }, [task]);

  if (!task) {
    return null;
  }

  return (
    <TaskDialog
      open={!!task}
      initialValues={initialValues}
      isLoading={false}
      isSubmitting={loading}
      onClose={onClose}
      onSubmit={(data) =>
        onAccept({
          ...data,
          id: task.id,
          startDate: data.deadline?.toISOString(),
        })
      }
      cancelButtonText="Dismiss Suggestion"
      showStatusControls={true}
      onCancelClick={() => onDismiss(task)}
      cancelButtonDisabled={loading || readonly}
      submitButtonText="Accept Task"
      submitAlwaysEnabled={!readonly}
    />
  );
};
